<template>
  <!-- Rich-card preview of a release/track/artist embedded inside a chat
       message. Renders inline next to the message body — clicking
       navigates to the asset detail page in the correct panel. Compact
       form factor (64px artwork tile) so it doesn't dominate the bubble. -->
  <NuxtLink
    v-if="data"
    :to="targetHref"
    class="block mt-2 not-italic no-underline group/share rounded-xl bg-[var(--surface-color)] border border-white/[0.08] hover:border-primary/30 hover:bg-white/[0.03] transition-colors overflow-hidden"
  >
    <div class="flex items-center gap-3 p-2 pr-3">
      <!-- Artwork tile (or fallback icon) -->
      <div
        class="relative shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-[#0a0b0f] border border-white/[0.06]"
      >
        <img
          v-if="data.artworkUrl && !imgError"
          :src="data.artworkUrl"
          :alt="data.title"
          class="w-full h-full object-cover"
          loading="lazy"
          @error="imgError = true"
        />
        <div v-else class="w-full h-full flex items-center justify-center text-text-muted">
          <el-icon size="22">
            <component :is="fallbackIcon" />
          </el-icon>
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <!-- Type chip — uppercase keep, semantic marker -->
        <div class="flex items-center gap-1.5 mb-0.5">
          <span
            class="upper-keep text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded uppercase"
            :class="typeChipClass"
          >
            {{ typeLabel }}
          </span>
          <span
            v-if="statusPill"
            class="upper-keep text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-full uppercase"
            :class="statusPill.cls"
          >
            {{ statusPill.label }}
          </span>
        </div>
        <p class="text-sm text-white font-semibold truncate group-hover/share:text-primary transition-colors">
          {{ data.title }}
        </p>
        <p v-if="data.subtitle" class="text-[11px] text-text-muted truncate">
          {{ data.subtitle }}
        </p>
        <p v-if="data.meta" class="text-[10px] text-text-dim font-mono mt-0.5 truncate">
          {{ data.meta }}
        </p>
      </div>

      <!-- Open chevron — affordance that the card is clickable -->
      <el-icon size="14" class="text-text-muted group-hover/share:text-primary shrink-0">
        <ArrowRight />
      </el-icon>
    </div>
  </NuxtLink>

  <!-- Failed-fetch fallback: render the URL as a regular link so the
       user still sees something useful (e.g. asset deleted or on a
       different tenant). -->
  <a
    v-else-if="error"
    :href="rawUrl"
    target="_blank"
    rel="noopener noreferrer"
    class="block mt-2 text-xs text-text-muted hover:text-primary underline truncate"
  >
    {{ rawUrl }}
  </a>
</template>

<script setup lang="ts">
import { Headset, Microphone, Notification, ArrowRight } from '@element-plus/icons-vue'

type Type = 'release' | 'track' | 'artist'
type CardData = {
  id: string
  type: Type
  title: string
  subtitle: string
  artworkUrl: string | null
  status: string | null
  meta: string | null
}

const props = defineProps<{
  type: Type
  id: string
  /** Original URL — used as fallback link when the resolve fetch fails. */
  rawUrl: string
}>()

const data = ref<CardData | null>(null)
const error = ref<string | null>(null)
const imgError = ref(false)

// Lazy fetch on mount — keeps the message payload tiny while still
// showing rich previews. If the asset doesn't exist (deleted, wrong
// tenant), the resolve endpoint 404s and we fall back to a plain link.
onMounted(async () => {
  try {
    const res = await $fetch<CardData>('/api/chat/share/resolve', {
      query: { type: props.type, id: props.id }
    })
    data.value = res
  } catch (e: any) {
    error.value = e?.statusMessage || 'failed'
  }
})

const typeLabel = computed(() => {
  if (props.type === 'release') return 'Release'
  if (props.type === 'track') return 'Track'
  return 'Artist'
})

const typeChipClass = computed(() => {
  if (props.type === 'release') return 'bg-primary/[0.10] text-primary border border-primary/25'
  if (props.type === 'track') return 'bg-violet-400/15 text-violet-300 border border-violet-400/25'
  return 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/25'
})

const fallbackIcon = computed(() => {
  if (props.type === 'track') return Headset
  if (props.type === 'artist') return Microphone
  return Notification
})

// Release lifecycle states → refined pill palette. Same source-of-truth
// the admin/releases listing uses; keeps the visual language consistent
// when an LM mentions a release in chat.
const statusPill = computed(() => {
  const s = data.value?.status
  if (!s) return null
  switch (s) {
    case 'LIVE':
      return { label: 'Live', cls: 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/25' }
    case 'DELIVERED':
    case 'DELIVERED_TO_FUGA':
      return { label: 'Delivered', cls: 'bg-blue-400/15 text-blue-300 border border-blue-400/25' }
    case 'TAKEDOWN':
      return { label: 'Takedown', cls: 'bg-rose-400/15 text-rose-300 border border-rose-400/25' }
    case 'PENDING':
      return { label: 'Pending', cls: 'bg-orange-400/15 text-orange-300 border border-orange-400/25 animate-pulse' }
    case 'DRAFT':
      return { label: 'Draft', cls: 'bg-amber-400/15 text-amber-300 border border-amber-400/25' }
    default:
      return { label: s.toLowerCase(), cls: 'bg-white/5 text-text-muted border border-white/10' }
  }
})

// Route prefix is dropped per CLAUDE.md guidance — Nuxt's router resolves
// /releases/:id, /tracks/:id, /artists/:id relative to the current panel
// (admin/creator/scouts/labels). One link string works across every role.
const targetHref = computed(() => {
  if (props.type === 'release') return `/releases/${props.id}`
  if (props.type === 'track') return `/tracks/${props.id}`
  return `/artists/${props.id}`
})
</script>
